import React from 'react';
import { useForm, useFieldArray, Controller } from 'react-hook-form';
import styled from 'styled-components';
import { IMaskInput } from 'react-imask';
import { useTheme } from '../../context/ThemeContext';
import Button from '../UI/Button';
import { StepProps, PartnerData } from '../../types';
import { Plus, X } from 'lucide-react';

interface Step4Data {
  partners: PartnerData[];
}

const Form = styled.form`
  display: flex;
  flex-direction: column;
  gap: 1.5rem;
`;

const HeaderContainer = styled.div`
  display: flex;
  flex-direction: column;
  gap: 0.5rem;
  margin-bottom: 2rem;
  text-align: center;
`;

const Title = styled.h2<{ theme: any }>`
  font-size: 1.5rem;
  font-weight: 600;
  color: ${props => props.theme.colors.text};
  margin: 0;
`;

const Description = styled.p<{ theme: any }>`
  color: ${props => props.theme.colors.textSecondary};
  margin: 0;
  line-height: 1.5;
`;

const InputGroup = styled.div`
  display: flex;
  flex-direction: column;
  gap: 0.5rem;
  width: 100%;
  min-width: 0; /* Prevents overflow */
  box-sizing: border-box;
`;

const SelectGroup = styled.div`
  display: flex;
  flex-direction: column;
  gap: 0.5rem;
  width: 100%;
  min-width: 0; /* Prevents overflow */
  box-sizing: border-box;
`;

const Label = styled.label<{ theme: any }>`
  font-weight: 500;
  color: ${props => props.theme.colors.text};
  font-size: 0.875rem;
`;

const Select = styled.select.withConfig({
  shouldForwardProp: (prop) => !['theme', 'hasError'].includes(prop),
})<{ theme: any; hasError: boolean }>`
  padding: 0.75rem;
  border: 2px solid ${props => props.hasError ? props.theme.colors.error : props.theme.colors.border};
  border-radius: 8px;
  font-size: 1rem;
  font-family: 'Raleway', sans-serif;
  background-color: ${props => props.theme.colors.background};
  color: ${props => props.theme.colors.text};
  transition: all 0.3s ease;

  &:focus {
    outline: none;
    border-color: ${props => props.hasError ? props.theme.colors.error : props.theme.colors.primary};
    box-shadow: 0 0 0 3px ${props => props.hasError ? props.theme.colors.error : props.theme.colors.primary}33;
  }
`;

const ErrorMessage = styled.span<{ theme: any }>`
  color: ${props => props.theme.colors.error};
  font-size: 0.75rem;
  margin-top: 0.25rem;
`;

const InfoMessage = styled.span<{ theme: any }>`
  color: ${props => props.theme.colors.textSecondary};
  font-size: 0.75rem;
  font-style: italic;
  opacity: 0.8;
`;

const PartnersContainer = styled.div`
  display: flex;
  flex-direction: column;
  gap: 1.5rem;
  margin-bottom: 1.5rem;
`;

const PartnerCard = styled.div<{ theme: any }>`
  border: 2px solid ${props => props.theme.colors.border};
  border-radius: 12px;
  padding: 1.5rem;
  background-color: ${props => props.theme.colors.surface};
  position: relative;
  width: 100%;
  box-sizing: border-box;
`;

const PartnerHeader = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-bottom: 1rem;
`;

const PartnerTitle = styled.h4<{ theme: any }>`
  color: ${props => props.theme.colors.text};
  margin: 0;
  font-size: 1rem;
  font-weight: 600;
`;

const RemoveButton = styled.button<{ theme: any }>`
  background: none;
  border: 2px solid ${props => props.theme.colors.error};
  border-radius: 50%;
  width: 32px;
  height: 32px;
  display: flex;
  align-items: center;
  justify-content: center;
  cursor: pointer;
  color: ${props => props.theme.colors.error};
  transition: all 0.3s ease;

  &:hover {
    background-color: ${props => props.theme.colors.error};
    color: white;
  }
`;

const PartnerFields = styled.div`
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(280px, 1fr));
  gap: 1rem;
  width: 100%;
  box-sizing: border-box;

  @media (max-width: 768px) {
    grid-template-columns: 1fr;
    gap: 1rem;
  }
`;

const AddPartnerButton = styled.button<{ theme: any }>`
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 0.5rem;
  padding: 0.75rem 1rem;
  border: 2px dashed ${props => props.theme.colors.border};
  border-radius: 8px;
  background: none;
  color: ${props => props.theme.colors.text};
  font-family: 'Raleway', sans-serif;
  font-size: 0.875rem;
  cursor: pointer;
  transition: all 0.3s ease;

  &:hover {
    border-color: ${props => props.theme.colors.primary};
    color: ${props => props.theme.colors.primary};
  }
`;

const ButtonGroup = styled.div`
  display: flex;
  gap: 1rem;
  justify-content: space-between;
  margin-top: 2rem;

  @media (max-width: 768px) {
    flex-direction: column;
  }
`;

const professionalBodies = [
  { label: 'Institute of Chartered Accountants of Nigeria (ICAN)', value: 'ICAN' },
  { label: 'Association of National Accountants of Nigeria (ANAN)', value: 'ANAN' },
  { label: 'Chartered Institute of Taxation of Nigeria (CITN)', value: 'CITN' },
  { label: 'Nigerian Bar Association (NBA)', value: 'NBA' },
];

const Step4Partners: React.FC<StepProps> = ({ data, onNext, onPrevious }) => {
  const { theme } = useTheme();
  const {
    register,
    handleSubmit,
    control,
    formState: { errors },
  } = useForm<Step4Data>({
    defaultValues: {
      partners: (data.partners && data.partners.length > 0) ? data.partners : [
        {
          membership_number: '',
          email: '',
          phone_number: '',
          years_in_practice: 0,
          professional_body: '',
        }
      ],
    },
  });

  const { fields, append, remove } = useFieldArray({
    control,
    name: 'partners',
  });

  const onSubmit = (formData: Step4Data) => {
    onNext(formData);
  };

  const addPartner = () => {
    append({
      membership_number: '',
      email: '',
      phone_number: '',
      years_in_practice: 0,
      professional_body: '',
    });
  };

  const removePartner = (index: number) => {
    if (fields.length > 1) {
      remove(index);
    }
  };

  return (
    <Form onSubmit={handleSubmit(onSubmit)}>
      <HeaderContainer>
        <Title theme={theme}>Partners Information</Title>
        <Description theme={theme}>
          Please provide information about your business partners. At least one partner is required.
        </Description>
        </HeaderContainer>

        <PartnersContainer>
          {fields.map((field, index) => (
            <PartnerCard key={field.id} theme={theme}>
              <PartnerHeader>
                <PartnerTitle theme={theme}>Partner {index + 1}</PartnerTitle>
                {fields.length > 1 && (
                  <RemoveButton
                    type="button"
                    theme={theme}
                    onClick={() => removePartner(index)}
                  >
                    <X size={16} />
                  </RemoveButton>
                )}
              </PartnerHeader>

          <PartnerFields>
            <InputGroup>
              <Label theme={theme}>
                Membership Number <span style={{ color: theme.colors.error }}>*</span>
              </Label>
              <input
                type="text"
                placeholder="Enter membership number"
                style={{
                  padding: '0.75rem',
                  border: `2px solid ${errors.partners?.[index]?.membership_number ? theme.colors.error : theme.colors.border}`,
                  borderRadius: '8px',
                  fontSize: '1rem',
                  fontFamily: 'Raleway, sans-serif',
                  backgroundColor: theme.colors.background,
                  color: theme.colors.text,
                  transition: 'all 0.3s ease',
                }}
                {...register(`partners.${index}.membership_number`, {
                  required: 'Membership number is required',
                })}
              />
              {errors.partners?.[index]?.membership_number && (
                <ErrorMessage theme={theme}>
                  {errors.partners[index]?.membership_number?.message}
                </ErrorMessage>
              )}
            </InputGroup>

            <InputGroup>
              <Label theme={theme}>
                Email Address <span style={{ color: theme.colors.error }}>*</span>
              </Label>
              <input
                type="email"
                placeholder="Enter email address"
                style={{
                  padding: '0.75rem',
                  border: `2px solid ${errors.partners?.[index]?.email ? theme.colors.error : theme.colors.border}`,
                  borderRadius: '8px',
                  fontSize: '1rem',
                  fontFamily: 'Raleway, sans-serif',
                  backgroundColor: theme.colors.background,
                  color: theme.colors.text,
                  transition: 'all 0.3s ease',
                }}
                {...register(`partners.${index}.email`, {
                  required: 'Email address is required',
                  pattern: {
                    value: /^[A-Z0-9._%+-]+@[A-Z0-9.-]+\.[A-Z]{2,}$/i,
                    message: 'Please enter a valid email address',
                  },
                })}
              />
              {errors.partners?.[index]?.email && (
                <ErrorMessage theme={theme}>
                  {errors.partners[index]?.email?.message}
                </ErrorMessage>
              )}
            </InputGroup>

            <InputGroup>
              <Label theme={theme}>
                Phone Number <span style={{ color: theme.colors.error }}>*</span>
              </Label>
              <Controller
                name={`partners.${index}.phone_number`}
                control={control}
                rules={{
                  required: 'Phone number is required',
                  validate: (value) => {
                    const cleanValue = value?.trim() || '';
                    if (!cleanValue) {
                      return 'Phone number is required';
                    }
                    if (cleanValue.length !== 11) {
                      return 'Phone number must be exactly 11 digits';
                    }
                    if (!/^\d{11}$/.test(cleanValue)) {
                      return 'Phone number must contain digits only';
                    }
                    return true;
                  },
                }}
                render={({ field }) => (
                  <IMaskInput
                    mask="00000000000"
                    placeholder="08012345678"
                    lazy={false}
                    value={field.value}
                    onAccept={(value) => {
                      field.onChange(value);
                    }}
                    style={{
                      padding: '0.75rem',
                      border: `2px solid ${errors.partners?.[index]?.phone_number ? theme.colors.error : theme.colors.border}`,
                      borderRadius: '8px',
                      fontSize: '1rem',
                      fontFamily: 'Raleway, sans-serif',
                      backgroundColor: theme.colors.background,
                      color: theme.colors.text,
                      transition: 'all 0.3s ease',
                    }}
                  />
                )}
              />
              <InfoMessage theme={theme}>Format: 11 digits (e.g., 08012345678)</InfoMessage>
              {errors.partners?.[index]?.phone_number && (
                <ErrorMessage theme={theme}>
                  {errors.partners[index]?.phone_number?.message}
                </ErrorMessage>
              )}
            </InputGroup>

            <InputGroup>
              <Label theme={theme}>
                Years in Practice <span style={{ color: theme.colors.error }}>*</span>
              </Label>
              <input
                type="number"
                placeholder="Enter years in practice"
                min="0"
                max="50"
                style={{
                  padding: '0.75rem',
                  border: `2px solid ${errors.partners?.[index]?.years_in_practice ? theme.colors.error : theme.colors.border}`,
                  borderRadius: '8px',
                  fontSize: '1rem',
                  fontFamily: 'Raleway, sans-serif',
                  backgroundColor: theme.colors.background,
                  color: theme.colors.text,
                  transition: 'all 0.3s ease',
                }}
                {...register(`partners.${index}.years_in_practice`, {
                  required: 'Years in practice is required',
                  min: {
                    value: 0,
                    message: 'Years in practice cannot be negative',
                  },
                  max: {
                    value: 50,
                    message: 'Years in practice cannot exceed 50',
                  },
                })}
              />
              {errors.partners?.[index]?.years_in_practice && (
                <ErrorMessage theme={theme}>
                  {errors.partners[index]?.years_in_practice?.message}
                </ErrorMessage>
              )}
            </InputGroup>

            <SelectGroup>
              <Label theme={theme}>
                Professional Body <span style={{ color: theme.colors.error }}>*</span>
              </Label>
              <Select
                theme={theme}
                hasError={!!errors.partners?.[index]?.professional_body}
                {...register(`partners.${index}.professional_body`, {
                  required: 'Professional body is required',
                })}
              >
                <option value="">Select professional body</option>
                {professionalBodies.map((body) => (
                  <option key={body.value} value={body.value}>
                    {body.label}
                  </option>
                ))}
              </Select>
              {errors.partners?.[index]?.professional_body && (
                <ErrorMessage theme={theme}>
                  {errors.partners[index]?.professional_body?.message}
                </ErrorMessage>
              )}
            </SelectGroup>
              </PartnerFields>
            </PartnerCard>
          ))}
        </PartnersContainer>

        <AddPartnerButton type="button" theme={theme} onClick={addPartner}>
        <Plus size={16} />
        Add Another Partner
      </AddPartnerButton>

      <ButtonGroup>
        <Button type="button" variant="secondary" onClick={onPrevious}>
          Previous
        </Button>
        <Button type="submit" variant="primary">
          Next
        </Button>
      </ButtonGroup>
    </Form>
  );
};

export default Step4Partners;
